<template>
    <div v-if="Object.keys(recommendedProducts).length" class="md:px-2 py-2">
        <p class="text-center md:text-left font-semibold text-gray-500">
            {{
                discount
                    ? `Add 1 more item to get ${discount.name.toLowerCase()}`
                    : "You Might Also Like"
            }}
        </p>

        <div class="grid grid-cols-2 -mx-4">
            <div v-for="product in recommendedProducts" :key="product.id">
                <div class="md:flex px-4 py-5 sm:p-6">
                    <div class="shrink-0">
                        <ProductIcon imgClass="mx-auto" :product="product" />
                    </div>

                    <div class="mt-2.5 md:mt-0 md:ml-3">
                        <p class="font-semibold text-sm px-1 pb-2">
                            <a
                                class="text-nutreance-green hover:text-nutreance-green-lighter"
                                :href="productPageHref(product)"
                            >
                                {{ product.name }} - {{ product.description }}
                                <span class="whitespace-nowrap"
                                    >({{
                                        product.default_product_option
                                            .description
                                    }})</span
                                >
                            </a>
                        </p>

                        <p class="text-gray-700 text-sm px-1 pb-2">
                            <span class="font-semibold">Price</span> ${{
                                dollarsForDisplay(
                                    product.default_product_option.price,
                                )
                            }}
                        </p>

                        <button
                            @click="addToCart(product)"
                            :name="`add_to_cart-${product.id}`"
                            type="button"
                            class="w-full mt-1 md:mx-1 py-2 shadow-sm rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-md focus:outline-none"
                        >
                            <span class="flex items-center justify-center">
                                <LoadingIcon
                                    v-if="state[product.id].loading"
                                    class="h-4 w-4 mr-2"
                                />
                                <ShoppingCartIcon
                                    v-else
                                    class="h-4 w-4 mr-2"
                                    aria-hidden="true"
                                />
                                Add to Cart
                            </span>
                        </button>

                        <p
                            v-if="state[product.id].error"
                            class="flex justify-center items-center text-sm text-red-600 my-2"
                        >
                            <XCircleIcon
                                class="h-4 w-4 mr-2 text-red-500"
                                aria-hidden="true"
                            />
                            {{ state[product.id].error }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, reactive, ref } from "vue";
import type { AppEmitter } from "../../emitter";
import { ShoppingCartIcon, XCircleIcon } from "@heroicons/vue/solid";
import { useMoney } from "../../functions/useMoney";
import ProductIcon from "../products/ProductIcon.vue";
import LoadingIcon from "../LoadingIcon.vue";
import { useGoogleDataLayer } from "../../functions/useGoogleDataLayer";
import { addCartItem } from "../../api/cart";
import { getProductOptions } from "../../api/products";

const props = defineProps<{
    initialRecommendedProducts?: any;
}>();

const emitter = inject<AppEmitter>("mitt")!;

const { dollarsForDisplay } = useMoney();
const { pushAddToCartToDataLayer, pushViewItemListToDataLayer } =
    useGoogleDataLayer();
const discount = ref<any>(null);
const state = reactive<Record<string, any>>({});

for (const key in props.initialRecommendedProducts) {
    const product = props.initialRecommendedProducts[key];

    state[product.id] = {
        loading: false,
        added: false,
        error: "",
    };
}

const recommendedProducts = computed(() => {
    const products: Record<string, any> = {};

    for (const key in props.initialRecommendedProducts) {
        const product = props.initialRecommendedProducts[key];

        if (!state[product.id].added) {
            products[key] = product;
        }
    }

    return products;
});
const firstRecommendedProduct = computed(() => {
    const keys = Object.keys(recommendedProducts.value);
    return keys.length ? recommendedProducts.value[keys[0]] : null;
});

updateDiscount();
pushViewItemListToDataLayer({
    products: props.initialRecommendedProducts,
    subscribe: false,
    source: "Recommended Products for Cart",
});

function productPageHref(recommendedProduct: any): string {
    return `/products/${recommendedProduct.name.toLowerCase()}`;
}

function addToCart(product: any): void {
    if (state[product.id].loading) {
        return;
    }

    state[product.id].loading = true;

    addCartItem({
        product_option_id: product.default_product_option.id,
        quantity: 1,
    })
        .then((data) => {
            state[product.id].error = "";
            state[product.id].added = true;

            emitter.emit("cartItemUpdated", data);
            pushAddToCartToDataLayer({
                product: product,
                subscribe: false,
                quantity: 1,
                source: "Recommended Products for Cart",
            });
        })
        .catch((error: any) => {
            if (
                typeof error.response !== "undefined" &&
                Object.prototype.hasOwnProperty.call(error.response, "data") &&
                Object.prototype.hasOwnProperty.call(
                    error.response.data,
                    "errors",
                )
            ) {
                const field = Object.keys(error.response.data.errors)[0];
                state[product.id].error = error.response.data.errors[field][0];
            }
        })
        .finally(() => (state[product.id].loading = false));
}

function updateDiscount(): void {
    if (!firstRecommendedProduct.value) {
        return;
    }

    getProductOptions({
        product_option_id:
            firstRecommendedProduct.value.default_product_option.id,
        quantity: 1,
    })
        .then((data) => {
            const discounts = Object.entries(data.discounts)
                .sort(
                    (
                        [, firstValue]: [string, any],
                        [, secondValue]: [string, any],
                    ) => secondValue.value - firstValue.value,
                )
                .reduce(
                    (
                        previous: Record<string, any>,
                        [, value]: [string, any],
                        key: number,
                    ) => {
                        previous[key] = value;
                        return previous;
                    },
                    {},
                );

            discount.value = discounts[Object.keys(discounts)[0]];
        })
        .catch(() => (discount.value = null));
}

onMounted(() => {
    emitter.on("cartItemUpdated", () => updateDiscount());
    emitter.on("shippingOptionUpdated", () => updateDiscount());
    emitter.on("shippingRegionUpdated", () => updateDiscount());
});
</script>
